import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { CoinGlyph } from './Coin';
import { Avatar } from './Avatar';
import { T, fmtAgo, t } from '../i18n';
import { feedName } from '../lib/api';

export interface FeedItem {
  username: string;
  coins: number;
  offer: string;
  provider?: string;
  at: number;
}

/** Live earnings over a hibernatable WebSocket (Durable Object), reconnecting with backoff. */
export function useLiveFeed() {
  const [items, setItems] = useState<FeedItem[]>([]);
  useEffect(() => {
    let ws: WebSocket | null = null;
    let tries = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let closed = false;
    const connect = () => {
      const proto = location.protocol === 'https:' ? 'wss' : 'ws';
      ws = new WebSocket(`${proto}://${location.host}/rt/feed`);
      ws.onopen = () => (tries = 0);
      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data as string) as {
            type: string;
            items?: FeedItem[];
            item?: FeedItem;
          };
          if (data.type === 'snapshot' && data.items) setItems(data.items);
          if (data.type === 'item' && data.item)
            setItems((prev) => [data.item!, ...prev].slice(0, 30));
          // Several earnings from the same second, newest first (the feed sends once a second).
          if (data.type === 'items' && data.items)
            setItems((prev) => [...data.items!, ...prev].slice(0, 30));
        } catch {
          /* ignore */
        }
      };
      ws.onclose = () => {
        if (closed) return;
        tries++;
        timer = setTimeout(connect, Math.min(30_000, 1000 * 2 ** tries));
      };
    };
    connect();
    return () => {
      closed = true;
      if (timer) clearTimeout(timer);
      ws?.close();
    };
  }, []);
  return items;
}

/** How long ago, short, in the reader's language ("5m ago"): timeAgo in lib/api. */
export const ago = (at: number): string => fmtAgo(at);

/**
 * One earning as a whole sentence: "alice earned 1,250 coins on Word Puzzle". The name (a username)
 * and the offer (a partner's name for it) are data, never translated; `name` and `offer` style them.
 */
function Earned({
  it,
  name,
  amount,
  offer,
}: {
  it: FeedItem;
  name: (username: string) => ReactNode;
  /** The amount's classes (bold, orange). */
  amount: string;
  offer: (offer: string) => ReactNode;
}) {
  return (
    // i18n: live feed of earnings: {name} is a username (gender unknown), {offer} the partner's name for the game, survey or app completed; <b> is the amount, in orange
    <T
      msg="{name} earned <b>{coins, plural, one {# coin} other {# coins}}</b> on {offer}"
      values={{ name: name(feedName(it.username)), coins: it.coins, offer: offer(it.offer) }}
      tags={{ b: (c) => <b className={amount}>{c}</b> }}
    />
  );
}

function Entry({ it }: { it: FeedItem }) {
  return (
    <div className="flex shrink-0 items-center gap-2 text-[13px]">
      <CoinGlyph className="h-4 w-4" />
      <span className="whitespace-nowrap text-white/60">
        <Earned
          it={it}
          name={(c) => (
            <span className="inline-block max-w-[140px] truncate align-bottom font-bold text-white">
              {c}
            </span>
          )}
          amount="font-bold tabular-nums text-coin"
          offer={(c) => (
            <span className="inline-block max-w-[160px] truncate align-bottom">{c}</span>
          )}
        />
      </span>
    </div>
  );
}

/**
 * Slim ink band of real earnings; pauses on hover. Hidden until there is activity. It only scrolls
 * when the entries are wider than the screen: scrolling needs a second copy to loop seamlessly,
 * and with a few entries that copy would read as duplicate earnings.
 */
export function LiveFeed() {
  const items = useLiveFeed();
  const boxRef = useRef<HTMLDivElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const [scroll, setScroll] = useState(false);
  useLayoutEffect(() => {
    const box = boxRef.current;
    const row = rowRef.current;
    if (!box || !row) return;
    const check = () => setScroll(row.scrollWidth > box.clientWidth);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(box);
    return () => ro.disconnect();
  }, [items]);
  if (items.length === 0) return null;
  return (
    <div
      ref={boxRef}
      role="region"
      className="group relative overflow-hidden bg-ink text-white"
      // i18n: name of the band of people's latest earnings, for screen readers
      aria-label={t('Live earnings')}
    >
      <div
        className="flex w-max items-center py-2.5 group-hover:[animation-play-state:paused]"
        style={
          scroll
            ? { animation: `ticker ${Math.max(40, items.length * 5)}s linear infinite` }
            : undefined
        }
      >
        {/* One copy, measured; a second only while scrolling, hidden from screen readers. The
            copies are identical (padding included) so sliding by half loops without a jump. */}
        <div ref={rowRef} className="flex items-center gap-8 pl-5 pr-3">
          {items.map((it, i) => (
            <Entry key={i} it={it} />
          ))}
        </div>
        {scroll && (
          <div className="flex items-center gap-8 pl-5 pr-3" aria-hidden>
            {items.map((it, i) => (
              <Entry key={i} it={it} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * The app version of the ticker (Earn, phones): one slim row that shows a single earning at a time
 * and swaps to the next every few seconds with a short rise. Static (latest entry) under reduced
 * motion, paused while the tab is hidden. Nothing renders until there is activity.
 */
export function LiveTicker({ className = '' }: { className?: string }) {
  const items = useLiveFeed();
  const [i, setI] = useState(0);
  useEffect(() => {
    if (items.length < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') setI((n) => n + 1);
    }, 3800);
    return () => window.clearInterval(timer);
  }, [items.length]);
  // The row is there from the first paint, with the first earning fading in when it arrives, so
  // the screen doesn't jump down a moment after it drew.
  const it = items.length ? items[i % items.length] : undefined;
  return (
    <div
      role="region"
      aria-label={t('Live earnings')}
      aria-busy={!it}
      className={`flex h-12 items-center gap-3 rounded-[14px] border border-hairline bg-paper pl-3 pr-3.5 ${className}`}
    >
      <span className="flex shrink-0 items-center gap-1.5 text-[12px] font-bold text-mint-deep">
        <span className="relative flex h-2 w-2" aria-hidden="true">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-mint opacity-60" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-mint" />
        </span>
        {
          // i18n: a "live" badge, like on a live broadcast: these earnings are happening now
          t('Live')
        }
      </span>
      <span aria-hidden="true" className="h-5 w-px shrink-0 bg-hairline" />
      <span className="relative h-full min-w-0 flex-1 overflow-hidden">
        {it && (
          <span
            key={i}
            className="absolute inset-0 flex items-center gap-2 text-[14px]"
            style={{ animation: i ? 'ticker-in 360ms var(--ease-out)' : 'fade-in 240ms ease-out' }}
          >
            <span className="hidden shrink-0 min-[360px]:inline-flex">
              <Avatar name={it.username} size={24} />
            </span>
            <span className="min-w-0 truncate text-graphite">
              <Earned
                it={it}
                name={(c) => <b className="font-bold text-ink">{c}</b>}
                amount="font-bold tabular-nums text-coin-ink"
                offer={(c) => c}
              />
            </span>
            <span className="ml-auto hidden shrink-0 pl-1 text-[12px] tabular-nums text-stone min-[375px]:inline">
              {ago(it.at)}
            </span>
          </span>
        )}
      </span>
    </div>
  );
}
